import { HEADER_TYPE } from "components/ai-editor/sections/Header";
import { MARKDONW_TYPE } from "components/ai-editor/sections/Markdown";
import { IMAGE_TYPE } from "components/ai-editor/sections/Image";
import { AI_TYPE } from "components/ai-editor/sections/AIBlock";

export const STYLES = {
  normal: "NORMAL_TEXT",
  title: "TITLE",
  subtitle: "SUBTITLE",
  heading1: "HEADING_1",
  heading2: "HEADING_2",
  heading3: "HEADING_3",
};

const TYPE_TO_STYLE = {
  title: "HEADING_1",
  section: "HEADING_2",
  subsection: "HEADING_3",
};

const IMAGE_SIZE_PT = 400;

export function extractLinksAndReplace(markdown) {
  const linkRegex = /\[([^\]]+)]\(([^)]+)\)/g;
  const result = [];
  let output = "";
  let lastIndex = 0;

  let match;
  while ((match = linkRegex.exec(markdown)) !== null) {
    const [fullMatch, text, url] = match;
    const matchStart = match.index;
    const matchEnd = linkRegex.lastIndex;

    // Add text before the match
    output += markdown.slice(lastIndex, matchStart);
    const start = output.length;

    // Add the display text
    output += text;
    const end = output.length;

    // Save the mapping
    result.push({ start, end, url });

    lastIndex = matchEnd;
  }

  // Add the remaining text
  output += markdown.slice(lastIndex);

  return { text: output, links: result };
}

export default class GoogleDoc {
  constructor(documentId, accessToken) {
    this.documentId = documentId;
    this.accessToken = accessToken;
    this.baseuUrl = `https://docs.googleapis.com/v1/documents/${this.documentId}`;
  }

  getUrl(method) {
    return `${this.baseuUrl}:${method}`;
  }

  async writeSection(section) {
    switch (section.type) {
      case HEADER_TYPE.key:
        await this.addText({
          text: section.text, 
          style: TYPE_TO_STYLE[section.headerType]
        });
        break;
      case MARKDONW_TYPE.key:
        for (const paragraph of section.text.split("\n\n")) {
          // Need to process links
          const { text, links } = extractLinksAndReplace(paragraph);
          await this.addText({
            text,
            links,
          });
        }
        break;
      case IMAGE_TYPE.key:
        console.log(section);
        await this.addCenteredImage(section.image.imageUrl, section.altText);
        break;
      case AI_TYPE.key:
        for (const paragraph of section.text.split("\n\n")) {
          await this.addText({
            text: paragraph,
          });
        }
        break;
      default:
        console.log(`SKIP section ${section.type}`);
    }
  }

  async getDocumentEndIndex() {
    const response = await fetch(this.baseuUrl, {
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
      },
    });
    const doc = await response.json();
    const lastElement = doc.body.content[doc.body.content.length - 1];
    return lastElement.endIndex;
  }

  async getAppendIndex() {
    return (await this.getDocumentEndIndex()) - 1;
  }

  async addLink(linkText, url) {
    const startIndex = await this.getAppendIndex();
    const requests = [
      {
        insertText: {
          location: {
            index: startIndex,
          },
          text: linkText,
        },
      },
      {
        updateTextStyle: {
          range: {
            startIndex: startIndex,
            endIndex: startIndex + linkText.length + 1,
          },
          textStyle: {
            link: {
              url,
            },
          },
          fields: "link",
        },
      },
    ];
    return await this.batchUpdate(requests);
  }

  async convertToLink(startIndex, endIndex, url) {
    const requests = [
      {
        updateTextStyle: {
          range: {
            startIndex,
            endIndex,
          },
          textStyle: {
            link: {
              url,
            },
          },
          fields: "link",
        },
      },
    ];
    return await this.batchUpdate(requests);
  }

  async batchUpdate(requests) {
    const response = await fetch(this.getUrl("batchUpdate"), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        requests,
      }),
    });
    const data = await response.json();
    return data;
  }

  async addText({ text, style = STYLES.normal, newline = true, links = [] }) {
    if (newline) {
      text = text.trim() + "\n";
    }
    const startIndex = await this.getAppendIndex();
    const requests = [
      {
        insertText: {
          location: {
            index: startIndex,
          },
          text,
        },
      },
      {
        updateParagraphStyle: {
          range: {
            startIndex: startIndex,
            endIndex: startIndex + text.length + 1,
          },
          paragraphStyle: {
            namedStyleType: style,
            alignment: "START",
            indentStart: {
              magnitude: 0,
              unit: "PT",
            },
            indentFirstLine: {
              magnitude: 0,
              unit: "PT",
            },
          },
          fields: "indentStart,indentFirstLine,namedStyleType,alignment",
        },
      },
    ];
    links.forEach((link) => {
      requests.push({
        updateTextStyle: {
          range: {
            startIndex: startIndex + link.start,
            endIndex: startIndex + link.end,
          },
          textStyle: {
            link: {
              url: link.url,
            },
          },
          fields: "link",
        },
      });
    });
    return await this.batchUpdate(requests);
    // const response = await fetch(this.getUrl("batchUpdate"), {
    //   method: "POST",
    //   headers: {
    //     Authorization: `Bearer ${this.accessToken}`,
    //     "Content-Type": "application/json",
    //   },
    //   body: JSON.stringify({
    //     requests: ,
    //   }),
    // });
    // const data = await response.json();
    // return data;
  }

  async addCenteredImage(imageUrl, description = "") {
    try {
      const index = await this.getAppendIndex();
      console.log({
        updateTextStyle: {
          range: {
            startIndex: index + 2,
            endIndex: index + 2 + description.length - 1,
          },
          textStyle: {
            italic: true,
          },
          fields: "italic",
        },
      });
      const response = await fetch(this.getUrl("batchUpdate"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.accessToken}`,
        },
        body: JSON.stringify({
          requests: [
            {
              insertInlineImage: {
                uri: imageUrl,
                location: {
                  index,
                },
                objectSize: {
                  height: {
                    magnitude: IMAGE_SIZE_PT,
                    unit: "PT",
                  },
                  width: {
                    magnitude: IMAGE_SIZE_PT,
                    unit: "PT",
                  },
                },
              },
            },
            {
              updateParagraphStyle: {
                range: {
                  startIndex: index,
                  endIndex: index + 1,
                },
                paragraphStyle: {
                  alignment: "CENTER",
                },
                fields: "alignment",
              },
            },
            {
              insertText: {
                location: {
                  index: index + 1,
                },
                text: "\n",
              },
            },
            {
              insertText: {
                location: {
                  index: index + 2,
                },
                text: description + "\n",
              },
            },
            {
              updateTextStyle: {
                range: {
                  startIndex: index + 2,
                  endIndex: index + 2 + description.length,
                },
                textStyle: {
                  italic: true,
                },
                fields: "italic",
              },
            },
          ],
        }),
      });
      const insertImageData = await response.json();
      const imageId = insertImageData.replies[0].insertInlineImage.objectId;
      return imageId;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  async addParagraphSpacing() {
    try {
      const index = await this.getAppendIndex();
      const response = await fetch(this.getUrl("batchUpdate"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.accessToken}`,
        },
        body: JSON.stringify({
          requests: [
            {
              updateParagraphStyle: {
                range: {
                  startIndex: 1,
                  endIndex: index,
                },
                paragraphStyle: {
                  spaceBelow: {
                    magnitude: 10,
                    unit: "PT",
                  },
                },
                fields: "spaceBelow",
              },
            },
          ],
        }),
      });
    } catch (error) {
      console.log(error);
    }
  }

  async addInlineImage(imageUrl) {
    try {
      const index = await this.getAppendIndex();
      const response = await fetch(this.getUrl("batchUpdate"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.accessToken}`,
        },
        body: JSON.stringify({
          requests: [
            {
              insertInlineImage: {
                uri: imageUrl,
                location: {
                  index,
                },
                objectSize: {
                  height: {
                    magnitude: IMAGE_SIZE_PT,
                    unit: "PT",
                  },
                  width: {
                    magnitude: IMAGE_SIZE_PT,
                    unit: "PT",
                  },
                },
              },
            },
          ],
        }),
      });

      const insertImageData = await response.json();

      // Retrieve the object ID of the inserted image
      const imageId = insertImageData.replies[0].insertInlineImage.objectId;

      console.log("Added image with id ", imageUrl);
      return imageId;
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  }
}
